import { z } from 'zod'
import type { BlockNode, LessonZoneEntry } from '@/lib/strapi/schemas'

export const courseDifficultySchema = z.enum(['beginner', 'intermediate', 'advanced'])
export type CourseDifficulty = z.infer<typeof courseDifficultySchema>

export const courseAgeGroupSchema = z.enum(['child', 'teen', 'adult', 'all'])
export type CourseAgeGroup = z.infer<typeof courseAgeGroupSchema>

export const courseAuthorSchema = z.object({
  name: z.string().min(1),
  role: z.string().min(1),
})
export type CourseAuthor = z.infer<typeof courseAuthorSchema>

export const accessibilityFeaturesSchema = z.object({
  signLanguage: z.boolean(),
  captions: z.boolean(),
  audioDescription: z.boolean(),
  easyRead: z.boolean(),
})
export type AccessibilityFeatures = z.infer<typeof accessibilityFeaturesSchema>

export const CourseSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().min(1).max(160),
  easyReadSummary: z.string().min(1),
  category: z.string().min(1),
  categoryLabel: z.string().min(1),
  difficulty: courseDifficultySchema,
  ageGroup: courseAgeGroupSchema,
  estimatedMinutes: z.number().int().positive(),
  accessibilityFeatures: accessibilityFeaturesSchema,
  authors: z.array(courseAuthorSchema).min(1),
  coverEmoji: z.string().min(1),
  coverImageUrl: z.string().optional(),
  coverImageAlt: z.string().optional(),
  featured: z.boolean(),
})
export type Course = z.infer<typeof CourseSchema>

export const difficultyLabels: Record<CourseDifficulty, string> = {
  beginner: 'Početni',
  intermediate: 'Srednji',
  advanced: 'Napredni',
}

/**
 * Labele opisuju SADRŽAJ kursa, ne korisnika, i namjerno nemaju raspone godina.
 * Platforma nigdje ne pita za uzrast; „Sadržaj za decu" je oznaka police u
 * katalogu, a ne tvrdnja o tome ko smije da ga otvori.
 */
export const ageGroupLabels: Record<CourseAgeGroup, string> = {
  child: 'Sadržaj za decu',
  teen: 'Sadržaj za tinejdžere',
  adult: 'Sadržaj za odrasle',
  all: 'Svi',
}

export type LessonContentBlock = BlockNode

export interface Lesson {
  documentId: string
  slug: string
  title: string
  order: number
  summary?: string
  content: LessonZoneEntry[]
  easyReadContent: LessonZoneEntry[]
  estimatedMinutes?: number
  courseSlug: string
}

// Quiz tipovi (autoritativni — sa isCorrect, samo server-side)

export type QuizQuestionType = 'single-choice' | 'true-false'

export interface QuizOption {
  id: number
  text: string
  easyReadText: string
  isCorrect: boolean
  explanation: string | null
}

export interface QuizQuestion {
  id: number
  text: string
  easyReadText: string
  type: QuizQuestionType
  hint: string | null
  options: QuizOption[]
}

export interface Quiz {
  id: number
  documentId: string
  title: string
  passingScore: number
  questions: QuizQuestion[]
}

// Public tipovi — bez isCorrect/explanation, sigurni za klijent

export interface PublicQuizOption {
  id: number
  text: string
  easyReadText: string
}

export interface PublicQuizQuestion {
  id: number
  text: string
  easyReadText: string
  type: QuizQuestionType
  hint: string | null
  options: PublicQuizOption[]
}

export interface PublicQuiz {
  id: number
  documentId: string
  title: string
  passingScore: number
  questions: PublicQuizQuestion[]
}

// Submit rezultat

export interface QuestionBreakdown {
  questionId: number
  questionText: string
  userOptionId: number | null
  correctOptionId: number
  isCorrect: boolean
  explanation: string | null
}

export interface SubmitQuizResult {
  score: number
  total: number
  percentage: number
  passed: boolean
  passingScore: number
  perQuestion: QuestionBreakdown[]
}
